import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import {
  collection, getDocs, doc, updateDoc, query, orderBy
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton,
  TextField, Grid, Chip, Divider, Table, TableHead,
  TableBody, TableRow, TableCell, Tooltip, LinearProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';


function fmt(val) {
  if (!val) return '–';
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

export default function OffersDashboard() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [offers, setOffers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDept, setEditingDept] = useState(null);
  const [deptEdits, setDeptEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [offerSnap, deptSnap] = await Promise.all([
      getDocs(query(collection(db, 'offers'), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, 'departments')),
    ]);
    setOffers(offerSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(
      d => d.name.toLowerCase() !== 'admin'
    ));
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const yearOffers = offers.filter(o => {
    const raw = o.createdAt;
    const d = raw?.toDate ? raw.toDate() : raw ? new Date(raw) : null;
    return d && d.getFullYear() === year;
  });

  const totalValue = yearOffers.reduce((s, o) => s + (o.estimatedValue || 0), 0);
  const wonValue = yearOffers
    .filter(o => o.status === 'accepted')
    .reduce((s, o) => s + (o.estimatedValue || 0), 0);
  const sentCount = yearOffers.filter(o => ['sent', 'accepted', 'declined', 'expired'].includes(o.status)).length;
  const acceptedCount = yearOffers.filter(o => o.status === 'accepted').length;
  const hitRate = sentCount > 0 ? Math.round((acceptedCount / sentCount) * 100) : null;

  const byDept = departments.map(dept => {
    const dos = yearOffers.filter(o => (o.departmentIds || []).includes(dept.id));
    const dSent = dos.filter(o => ['sent', 'accepted', 'declined', 'expired'].includes(o.status)).length;
    const dWon = dos.filter(o => o.status === 'accepted').length;
    return {
      dept,
      total: dos.length,
      sent: dSent,
      accepted: dWon,
      declined: dos.filter(o => o.status === 'declined').length,
      value: dos.reduce((s, o) => s + (o.estimatedValue || 0), 0),
      wonValue: dos.filter(o => o.status === 'accepted').reduce((s, o) => s + (o.estimatedValue || 0), 0),
      hitRate: dSent > 0 ? Math.round((dWon / dSent) * 100) : null,
    };
  });

  function startEdit(dept) {
    setDeptEdits(prev => ({
      ...prev,
      [dept.id]: { offerCode: dept.offerCode || '', offerStartNumber: dept.offerStartNumber || 1 }
    }));
    setEditingDept(dept.id);
  }

  async function saveDept(deptId) {
    const edits = deptEdits[deptId] || {};
    setSaving(true);
    const updates = {
      offerCode: (edits.offerCode || '').trim().toUpperCase(),
      offerStartNumber: Number(edits.offerStartNumber) || 1,
    };
    await updateDoc(doc(db, 'departments', deptId), updates);
    setDepartments(prev => prev.map(d => d.id === deptId ? { ...d, ...updates } : d));
    setEditingDept(null);
    setSaving(false);
  }

  const yearRange = [];
  for (let y = currentYear + 1; y >= 2023; y--) yearRange.push(y);

  const QUICK_LINKS = [
    { label: 'Offertlista', path: '/offers', icon: <DescriptionIcon sx={{ fontSize: 18 }} /> },
    { label: 'Mallar', path: '/offer-templates', icon: <DescriptionIcon sx={{ fontSize: 18 }} /> },
    { label: 'PlingKlang / Leads', path: '/crm/pling-klang', icon: <TrendingUpIcon sx={{ fontSize: 18 }} /> },
    { label: 'CRM-företag', path: '/crm', icon: <OpenInNewIcon sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/offers')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">Offertöversikt</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Statistik, nyckeltal och AO-inställningar
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {yearRange.map(y => (
            <Chip key={y} label={y}
              onClick={() => setYear(y)}
              size="small"
              sx={{
                cursor: 'pointer',
                bgcolor: y === year ? '#FF6B00' : 'rgba(255,255,255,0.1)',
                color: 'white',
                fontWeight: y === year ? 700 : 400,
              }} />
          ))}
        </Box>
      </Box>
      {loading && <LinearProgress sx={{ bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />}

      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* ── Årsöversikt ── */}
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
          Årsöversikt {year}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Offerter totalt', value: yearOffers.length, color: '#FF6B00' },
            { label: 'Skickade', value: sentCount, color: '#1565C0' },
            { label: 'Accepterade', value: acceptedCount, color: '#2E7D32' },
            { label: 'Nekade', value: yearOffers.filter(o => o.status === 'declined').length, color: '#C62828' },
            { label: 'Hit rate', value: hitRate !== null ? hitRate + '%' : '–', color: '#6A1B9A' },
            { label: 'Offererat värde', value: fmt(totalValue), color: '#FF6B00', wide: true },
            { label: 'Vunnet värde', value: fmt(wonValue), color: '#2E7D32', wide: true },
          ].map((s, i) => (
            <Grid item xs={6} sm={s.wide ? 4 : 2} key={i}>
              <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <Typography variant={s.wide ? 'h6' : 'h5'} fontWeight="bold" sx={{ color: s.color, lineHeight: 1.1 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Per AO ── */}
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
          Per affärsområde – {year}
        </Typography>
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['Affärsområde', 'Totalt', 'Skickade', 'Accepterade', 'Nekade', 'Hit rate', 'Offererat värde', 'Vunnet värde'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {byDept.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="caption" color="text.secondary">Inga affärsområden konfigurerade</Typography>
                  </TableCell>
                </TableRow>
              ) : byDept.map(({ dept, total, sent, accepted, declined, value, wonValue: wv, hitRate: hr }) => (
                <TableRow key={dept.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{dept.name}</TableCell>
                  <TableCell>{total}</TableCell>
                  <TableCell>{sent}</TableCell>
                  <TableCell sx={{ color: '#2E7D32', fontWeight: accepted > 0 ? 600 : 400 }}>{accepted}</TableCell>
                  <TableCell sx={{ color: declined > 0 ? '#C62828' : 'inherit' }}>{declined}</TableCell>
                  <TableCell>{hr !== null ? hr + '%' : '–'}</TableCell>
                  <TableCell>{total > 0 ? fmt(value) : '–'}</TableCell>
                  <TableCell sx={{ color: '#2E7D32', fontWeight: wv > 0 ? 600 : 400 }}>
                    {wv > 0 ? fmt(wv) : '–'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* ── AO-inställningar ── */}
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
          AO-inställningar – offertnummer
        </Typography>
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              Offertnummer genereras automatiskt i formatet <strong>KOD-ÅÅ-NNN</strong> (ex <strong>BTM-26-001</strong>).
              Ange en 2–4-bokstavskod per AO. Löpnumret återstartar vid nytt år.
            </Typography>
          </Box>
          <Divider />
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['Affärsområde', 'AO-kod', 'Startnummer (innevarande år)', 'Förhandsgranskning', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map(dept => {
                const isEditing = editingDept === dept.id;
                const edits = deptEdits[dept.id] || {};
                const code = isEditing ? edits.offerCode : dept.offerCode;
                const startNum = isEditing ? edits.offerStartNumber : dept.offerStartNumber;
                const yy = String(currentYear).slice(-2);
                const preview = code
                  ? `${code}-${yy}-${String(startNum || 1).padStart(3, '0')}`
                  : '–';
                return (
                  <TableRow key={dept.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{dept.name}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField size="small" value={edits.offerCode || ''}
                          onChange={e => setDeptEdits(p => ({
                            ...p, [dept.id]: { ...p[dept.id], offerCode: e.target.value }
                          }))}
                          inputProps={{ maxLength: 6, style: { textTransform: 'uppercase', width: 80 } }}
                          placeholder="ex BTM" />
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {dept.offerCode || <span style={{ color: '#aaa' }}>Ej satt</span>}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField size="small" type="number" value={edits.offerStartNumber || 1}
                          onChange={e => setDeptEdits(p => ({
                            ...p, [dept.id]: { ...p[dept.id], offerStartNumber: e.target.value }
                          }))}
                          inputProps={{ min: 1, style: { width: 80 } }} />
                      ) : (
                        <Typography variant="body2">{dept.offerStartNumber || 1}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#FF6B00' }}>
                        {preview}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Spara">
                            <IconButton size="small" onClick={() => saveDept(dept.id)} disabled={saving}>
                              <CheckIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Avbryt">
                            <IconButton size="small" onClick={() => setEditingDept(null)}>
                              <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Tooltip title="Redigera">
                          <IconButton size="small" onClick={() => startEdit(dept)}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        {/* ── Snabblänkar ── */}
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
          Snabblänkar
        </Typography>
        <Grid container spacing={2}>
          {QUICK_LINKS.map(link => (
            <Grid item xs={6} sm={3} key={link.path}>
              <Paper
                onClick={() => navigate(link.path)}
                sx={{ p: 2, cursor: 'pointer', display: 'flex', alignItems: 'center',
                  gap: 1.5, '&:hover': { boxShadow: 3 }, transition: 'box-shadow .15s',
                  borderLeft: '3px solid #FF6B00' }}>
                <Box sx={{ color: '#FF6B00' }}>{link.icon}</Box>
                <Typography variant="body2" fontWeight={600}>{link.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
}
